import { type FormEvent, type JSX, useEffect, useRef, useState } from 'react';

import { zweitenFaktorBeginnen, zweitenFaktorBestaetigen } from '../api/anmeldung';
import { ApiFehler } from '../api/fehler';
import { QrCode } from './QrCode';
import { Feld } from '../komponenten/Feld';
import { Meldung, Hinweis } from '../komponenten/Meldung';

/**
 * Einrichtung des zweiten Faktors (SYS-05, AA-003).
 *
 * KEIN QR-CODE, und das ist eine Entscheidung, keine Luecke: Ein QR-Bild
 * braucht eine Abhaengigkeit, die nur an dieser einen Stelle gebraucht wird.
 * Stattdessen zwei Wege, die ohne auskommen — der otpauth-Verweis oeffnet am
 * Telefon die App unmittelbar, und am Rechner steht das Geheimnis in
 * Vierergruppen zum Abtippen.
 *
 * Sobald die Oberflaeche ohnehin eine Bibliothek fuer Symbole bekommt, gehoert
 * der QR-Code nachgezogen — er ist der einzige Weg, den ein ungeuebter
 * Benutzer beim ersten Mal ohne Ruecksprache schafft.
 */
export function ZweiterFaktor({
  anmeldename,
  passwort,
  grund,
  beiFertig,
  beiAbbruch,
  titelVerweis,
}: {
  readonly anmeldename: string;
  readonly passwort: string;
  /** Warum der Anwender hier ist — sonst wechselt der Bildschirm grundlos. */
  readonly grund?: string | undefined;
  readonly beiFertig: () => void;
  readonly beiAbbruch: () => void;
  readonly titelVerweis?: React.Ref<HTMLHeadingElement>;
}): JSX.Element {
  const [geheimnis, setzeGeheimnis] = useState<string | undefined>(undefined);
  const [adresse, setzeAdresse] = useState<string | undefined>(undefined);
  const [code, setzeCode] = useState('');
  const [laeuft, setzeLaeuft] = useState(true);
  const [kopierstand, setzeKopierstand] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const codeFeld = useRef<HTMLInputElement>(null);
  const meldungKasten = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let abgebrochen = false;
    void (async () => {
      try {
        const ergebnis = await zweitenFaktorBeginnen({ anmeldename, passwort });
        if (abgebrochen) return;
        setzeGeheimnis(ergebnis.geheimnis);
        setzeAdresse(ergebnis.adresse);
      } catch (ursache) {
        if (!abgebrochen) setzeFehler(ursache as ApiFehler);
      } finally {
        if (!abgebrochen) setzeLaeuft(false);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [anmeldename, passwort]);

  /**
   * Fokus erst, wenn das Feld da IST.
   *
   * Frueher stand der Aufruf unmittelbar hinter setzeGeheimnis — dort existiert
   * das Eingabefeld noch nicht, es haengt an `geheimnis !== undefined`. Der
   * Aufruf lief ins Leere, und der Fokus blieb fuer die ganze Maske am
   * Seitenanfang (NFA-07).
   */
  useEffect(() => {
    if (geheimnis !== undefined) codeFeld.current?.focus();
  }, [geheimnis]);

  useEffect(() => {
    if (fehler !== undefined && geheimnis === undefined) meldungKasten.current?.focus();
  }, [fehler, geheimnis]);

  async function bestaetigen(ereignis: FormEvent): Promise<void> {
    ereignis.preventDefault();
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      await zweitenFaktorBestaetigen({ anmeldename, passwort, code });
      beiFertig();
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
      codeFeld.current?.focus();
      codeFeld.current?.select();
    } finally {
      setzeLaeuft(false);
    }
  }

  /**
   * Die Adresse kommt vom Server und landet in einem href.
   *
   * React maskiert Textinhalte, aber NICHT das href-Attribut: Eine
   * javascript:-Adresse wuerde gerendert und beim Klick im Ursprung der
   * Anwendung ausgefuehrt. Die Zusicherung, dass dort otpauth:// steht, gehoert
   * deshalb hierher — an die Stelle, an der sie wirkt, und nicht in das
   * Vertrauen auf die Gegenseite (NFA-08).
   */
  const zeigbareAdresse =
    adresse !== undefined && adresse.startsWith('otpauth://') ? adresse : undefined;

  /** Vierergruppen: so wird abgetippt und so wird vorgelesen. */
  const lesbar = geheimnis === undefined ? '' : (geheimnis.match(/.{1,4}/g) ?? []).join(' ');

  async function kopieren(): Promise<void> {
    if (geheimnis === undefined) return;
    try {
      if (navigator.clipboard === undefined) throw new Error('keine Zwischenablage');
      await navigator.clipboard.writeText(geheimnis);
      setzeKopierstand('In die Zwischenablage kopiert.');
    } catch {
      // Ein Knopf, der still nichts tut, ist schlimmer als keiner. Ohne
      // Zwischenablage-API — aelterer Browser, kein sicherer Ursprung — sagt
      // er, was stattdessen zu tun ist (ERR-01).
      setzeKopierstand('Kopieren geht in diesem Browser nicht. Bitte den Schlüssel abtippen.');
    }
  }

  return (
    <main className="maske" aria-labelledby="faktor-titel">
      <h1 id="faktor-titel" tabIndex={-1} ref={titelVerweis}>
        Zweiten Faktor einrichten
      </h1>
      <p className="maske__unterzeile">
        Für <strong>{anmeldename}</strong>.
      </p>

      {grund !== undefined && <Hinweis ueberschrift={grund} />}
      {fehler !== undefined && <Meldung fehler={fehler} gezeigteFelder={['code']} verweis={meldungKasten} />}

      {geheimnis === undefined && fehler === undefined && (
        <p role="status">Einrichtung wird vorbereitet …</p>
      )}

      {geheimnis !== undefined && (
        <>
          <ol className="schritte">
            <li>
              Öffnen Sie eine Authenticator-App — etwa Google Authenticator, Microsoft
              Authenticator oder die Ihres Passwortverwalters.
            </li>
            <li>
              Legen Sie dort einen neuen Eintrag an — am schnellsten, indem Sie diesen Code
              scannen:
              {/*
                DER QR-CODE IST DIE ABKÜRZUNG, NICHT DER EINZIGE WEG.
                Er steht vor dem Schlüssel, weil er der übliche Fall ist: Man
                sitzt am Rechner und hält das Telefon in der Hand. Der
                Schlüssel darunter bleibt — für alle, die das Bild nicht
                sehen, und für den Fall, dass die Kamera nicht mitspielt.
              */}
              {zeigbareAdresse !== undefined && (
                <div className="qrfeld">
                  <QrCode
                    inhalt={zeigbareAdresse}
                    beschreibung={
                      `QR-Code zum Einrichten des zweiten Faktors für ${anmeldename}. `
                      + 'Wer ihn nicht scannen kann, findet den Schlüssel als Text darunter.'
                    }
                  />
                </div>
              )}
              <p className="geheimnis__hinweis">
                Ohne Kamera: Geben Sie diesen Schlüssel von Hand ein.
              </p>
              <div className="geheimnis">
                <code className="geheimnis__wert">{lesbar}</code>
                <button
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => void kopieren()}
                >
                  Kopieren
                </button>
              </div>
              <p className="geheimnis__hinweis" role="status">
                {kopierstand}
              </p>
              {zeigbareAdresse !== undefined && (
                <p className="geheimnis__verweis">
                  Sind Sie am Telefon selbst?{' '}
                  <a href={zeigbareAdresse}>Direkt in der App öffnen</a>
                </p>
              )}
            </li>
            <li>Geben Sie den sechsstelligen Code ein, den die App anzeigt.</li>
          </ol>

          <form onSubmit={(e) => void bestaetigen(e)} noValidate>
            <Feld
              beschriftung="Code aus der App"
              wert={code}
              aendern={setzeCode}
              pflicht
              automatik="one-time-code"
              eingabeart="numeric"
              hinweisText="123456"
              verweis={codeFeld}
              verstoesse={fehler?.alleZumFeld('code') ?? []}
              hilfe="Der Code wechselt alle 30 Sekunden. Nehmen Sie den, der gerade angezeigt wird."
            />
            <div className="knopfreihe">
              <button className="knopf knopf--haupt" type="submit" disabled={laeuft}>
                {laeuft ? 'Wird geprüft …' : 'Einrichtung abschließen'}
              </button>
            </div>
          </form>

          <p className="warnung" role="note">
            Bewahren Sie den Schlüssel nicht auf. Verlieren Sie das Gerät, setzt die
            Administration den zweiten Faktor zurück — dafür braucht sie den Schlüssel
            nicht.
          </p>
        </>
      )}

      {/* Der Rueckweg steht IMMER offen — auch waehrend des Ladens und im
          Fehlerfall. Ohne ihn gab es Zustaende ganz ohne bedienbares Element:
          Haengt der Aufruf, kaeme weder Tastatur noch Maus zurueck, und nur ein
          Neuladen der Seite half (NFA-07). */}
      <div className="knopfreihe">
        <button className="knopf" type="button" onClick={beiAbbruch}>
          Zurück zur Anmeldung
        </button>
      </div>
    </main>
  );
}
